import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  TextField,
  Button,
  Divider,
  Chip,
  Stack,
  IconButton,
  InputAdornment,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import {
  Person,
  Email,
  Phone,
  Business,
  Badge,
  Lock,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Edit,
  Save,
  Cancel,
  PhotoCamera,
} from "@mui/icons-material";
import * as myApi from "../components/Api";
import { toast } from "react-toastify";
import { showLoading } from "react-global-loading";

const API_URL = myApi.baseUrl;

export default function MyProfile() {
  const [user, setUser] = useState({});
  const [profileData, setProfileData] = useState({
    name: "",
    phone: "",
    organization: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    password: "",
    confirm_password: "",
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const userData = myApi.getUser();
    if (userData) {
      setUser(userData);
      setProfileData({
        name: userData.name || "",
        phone: userData.phone || "",
        organization: userData.organization || "",
      });
    }
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await fetch(myApi.baseUrl + '/user/profile/', {
        headers: { 'Authorization': 'Bearer ' + myApi.getToken() }
      });
      const data = await res.json();
      console.log('[MyProfile] fetchProfile raw response:', data);
      const userData = data.result || data.data;
      if (userData) {
        console.log('[MyProfile] userData', userData);
        setUser(userData);
        myApi.setUser(userData);
        setProfileData({
          name: userData.name || "",
          phone: userData.phone || "",
          organization: userData.organization || "",
        });
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  const handleProfileEdit = () => {
    setIsEditing(true);
  };

  const handleProfileCancel = () => {
    setProfileData({
      name: user.name || "",
      phone: user.phone || "",
      organization: user.organization || "",
    });
    setIsEditing(false);
  };

  const handleProfileSave = async () => {
    if (!profileData.name || profileData.name.trim() === "") {
      toast.error("Name is required", { type: "error" });
      return;
    }

    showLoading(true);
    try {
      const response = await myApi.put("/user/profile/", profileData);
      if (response.data && response.data.status === "success") {
        toast.success("Profile updated successfully!", { type: "success" });
        const updatedUser = { ...user, ...profileData };
        setUser(updatedUser);
        myApi.setUser(updatedUser);
        setIsEditing(false);
      } else {
        toast.error(response.data?.message || "Failed to update profile", { type: "error" });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error updating profile", { type: "error" });
    } finally {
      showLoading(false);
    }
  };

  const handleProfileChange = (field) => (e) => {
    setProfileData({ ...profileData, [field]: e.target.value });
  };

  const handleProfilePictureClick = () => {
    fileInputRef.current.click();
  };

  const handleProfilePictureChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error("Please select a valid image file (jpeg, jpg, png, gif, webp)", { type: "error" });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size must be less than 2MB", { type: "error" });
      return;
    }

    setIsUploading(true);
    showLoading(true);

    const formData = new FormData();
    formData.append('profile_picture', file);

    try {
      const token = myApi.getToken();
      const response = await fetch(`${API_URL}/user/profile-picture/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const result = await response.json();

      if (result.status === 'success' || result.code === 200) {
        toast.success("Profile picture updated successfully!", { type: "success" });
        const updatedUser = { ...user, profile_picture: result.data.profile_picture };
        setUser(updatedUser);
        myApi.setUser(updatedUser);
      } else {
        toast.error(result.message || "Failed to upload profile picture", { type: "error" });
      }
    } catch (error) {
      toast.error("Error uploading profile picture", { type: "error" });
    } finally {
      setIsUploading(false);
      showLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getProfilePictureUrl = () => {
    const url = myApi.getProfilePictureUrl(user);
    console.log('[MyProfile] getProfilePictureUrl:', { 
      hasUser: !!user, 
      userObj: user,
      profile_picture: user?.profile_picture,
      url 
    });
    return url;
  };

  const handlePasswordChange = (event) => {
    event.preventDefault();
    showLoading(true);

    const data = {
      current_password: passwordData.current_password,
      password: passwordData.password,
      confirm_password: passwordData.confirm_password,
    };

    // Validate passwords
    const errors = {};

    if (!data.current_password || data.current_password.trim() === "") {
      errors.current_password = "Current password is required";
    }

    if (!data.password || data.password.trim() === "") {
      errors.password = "New password is required";
    } else if (data.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    if (!data.confirm_password || data.confirm_password.trim() === "") {
      errors.confirm_password = "Please confirm your password";
    } else if (data.password !== data.confirm_password) {
      errors.confirm_password = "Passwords do not match";
    }

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      showLoading(false);
      toast("Please fix the validation errors", { type: "error" });
      return;
    }

    setPasswordErrors({});

    myApi
      .post("/change-password/", data)
      .then((response) => {
        showLoading(false);
        toast("Password changed successfully!", { type: "success" });
        // Clear the form
        setPasswordData({
          current_password: "",
          password: "",
          confirm_password: "",
        });
        event.currentTarget.reset();
      })
      .catch((error) => {
        showLoading(false);
        toast(error.message || "Failed to change password", { type: "error" });
      });
  };

  const handlePasswordInputChange = (field) => (e) => {
    setPasswordData({ ...passwordData, [field]: e.target.value });
    if (passwordErrors[field]) {
      setPasswordErrors({ ...passwordErrors, [field]: "" });
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });
  };

  const getRoleChip = (role) => {
    const roleConfig = {
      admin: { label: "Administrator", color: "error" },
      peserta: { label: "Participant", color: "primary" },
    };
    const config = roleConfig[role] || { label: role, color: "default" };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        My Profile
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        View and manage your account information
      </Typography>

      <Grid container spacing={4}>
        {/* Profile Information Card */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box sx={{ position: "relative", mr: 3 }}>
                    <Avatar
                      src={user?.profile_picture || undefined}
                      onError={(e) => { 
                        console.log('[Avatar] onError:', e); 
                        e.target.src = undefined; 
                      }}
                      sx={{
                        width: 100,
                        height: 100,
                        fontSize: "2.5rem",
                        bgcolor: "grey.400",
                      }}
                    >
                      {user.name?.charAt(0) || <Person />}
                    </Avatar>
                    <IconButton
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        right: 0,
                        bgcolor: "background.paper",
                        "&:hover": { bgcolor: "grey.200" },
                        width: 32,
                        height: 32,
                      }}
                      size="small"
                      onClick={handleProfilePictureClick}
                      disabled={isUploading}
                    >
                      {isUploading ? (
                        <CircularProgress size={16} />
                      ) : (
                        <PhotoCamera fontSize="small" />
                      )}
                    </IconButton>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleProfilePictureChange}
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      style={{ display: "none" }}
                    />
                  </Box>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                      <Typography variant="h5" fontWeight={600}>
                        {user.name || "Unknown User"}
                      </Typography>
                      {getRoleChip(user.role)}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      User ID: {user.userid_code || "N/A"}
                    </Typography>
                  </Box>
                </Box>
                {!isEditing ? (
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={handleProfileEdit}
                  >
                    Edit Profile
                  </Button>
                ) : (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Cancel />}
                      onClick={handleProfileCancel}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<Save />}
                      onClick={handleProfileSave}
                    >
                      Save
                    </Button>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Person sx={{ mr: 2, color: "action.active" }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Full Name
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={profileData.name}
                          onChange={handleProfileChange("name")}
                          placeholder="Enter your name"
                        />
                      ) : (
                        <Typography variant="body1" fontWeight={500}>
                          {user.name || "Not provided"}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Email sx={{ mr: 2, color: "action.active" }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Email Address
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {user.email || "Not provided"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Phone sx={{ mr: 2, color: "action.active" }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Phone Number
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={profileData.phone}
                          onChange={handleProfileChange("phone")}
                          placeholder="Enter phone number"
                        />
                      ) : (
                        <Typography variant="body1" fontWeight={500}>
                          {user.phone || "Not provided"}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Business sx={{ mr: 2, color: "action.active" }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Organization
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={profileData.organization}
                          onChange={handleProfileChange("organization")}
                          placeholder="Enter organization"
                        />
                      ) : (
                        <Typography variant="body1" fontWeight={500}>
                          {user.organization || "Not provided"}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Badge sx={{ mr: 2, color: "action.active" }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Account Status
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {user.user_status === "active" ? (
                          <Chip
                            label="Active"
                            color="success"
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        ) : (
                          <Chip
                            label="Inactive"
                            color="warning"
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Change Password Card */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Lock sx={{ mr: 2, color: "primary.main" }} />
                <Typography variant="h6" fontWeight={600}>
                  Change Password
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                Please enter your current password and a new password to update
                your account security.
              </Alert>

              <form onSubmit={handlePasswordChange}>
                <Stack spacing={3} maxWidth="sm" sx={{ mx: "auto" }}>
                  <TextField
                    fullWidth
                    required
                    label="Current Password"
                    type={showPasswords.current ? "text" : "password"}
                    value={passwordData.current_password}
                    onChange={handlePasswordInputChange("current_password")}
                    error={!!passwordErrors.current_password}
                    helperText={passwordErrors.current_password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => togglePasswordVisibility("current")}
                            edge="end"
                          >
                            {showPasswords.current ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    required
                    label="New Password"
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordData.password}
                    onChange={handlePasswordInputChange("password")}
                    error={!!passwordErrors.password}
                    helperText={passwordErrors.password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => togglePasswordVisibility("new")}
                            edge="end"
                          >
                            {showPasswords.new ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    required
                    label="Confirm New Password"
                    type={showPasswords.confirm ? "text" : "password"}
                    value={passwordData.confirm_password}
                    onChange={handlePasswordInputChange("confirm_password")}
                    error={!!passwordErrors.confirm_password}
                    helperText={passwordErrors.confirm_password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => togglePasswordVisibility("confirm")}
                            edge="end"
                          >
                            {showPasswords.confirm ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={<CheckCircle />}
                    sx={{ mt: 2 }}
                  >
                    Change Password
                  </Button>
                </Stack>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
